/**
 * QuickExtract 图片处理器
 * 集成图片提取功能到主项目中，提供简化的API接口
 */

import fs from 'fs';
import path from 'path';
import type { 
  ImageProcessingConfig, 
  ImageProcessingResult, 
  ProcessedImage, 
  FailedImage, 
  ImageProcessingStats,
  ImageNode 
} from '../types/image-processing';
import { Logger } from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';

export interface QuickExtractConfig {
  /** Figma访问令牌 */
  accessToken: string;
  /** Figma文件ID */
  fileKey: string;
  /** 输出目录 */
  outputDir?: string;
  /** 图片格式 */
  format?: 'png' | 'jpg' | 'svg';
  /** 图片缩放 */
  scale?: number;
  /** 是否下载图片到本地 */
  downloadImages?: boolean;
  /** 是否生成详细报告 */
  generateReport?: boolean;
  /** 指定节点ID（可选，不指定则提取整个文件） */
  nodeId?: string;
}

export interface QuickExtractImage {
  /** 节点ID */
  nodeId: string;
  /** 节点名称 */
  nodeName: string;
  /** 节点类型 */
  nodeType: string;
  /** 图片尺寸和位置 */
  dimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** 图片URL */
  imageUrl?: string;
  /** 本地文件路径 */
  localPath?: string;
  /** quickExtract链接（处理后的图片链接） */
  quickExtract?: string;
  /** 图片哈希 */
  imageHash?: string;
  /** 图片引用 */
  imageRef?: string;
}

export interface QuickExtractResult {
  /** 提取的图片列表 */
  images: QuickExtractImage[];
  /** 原始Figma数据（增强版，包含images属性） */
  enhancedData: any;
  /** 提取统计信息 */
  stats: {
    totalImages: number;
    successfulDownloads: number;
    failedDownloads: number;
    processingTime: number;
  };
  /** 输出文件路径 */
  outputPaths: {
    imagesDir?: string;
    reportFile?: string;
    listFile?: string;
    summaryFile?: string;
  };
}

export class QuickExtractImageProcessor {
  private config: QuickExtractConfig;
  private logger: Logger;
  private extractedImages: QuickExtractImage[] = [];

  constructor(config: QuickExtractConfig) {
    this.config = {
      outputDir: 'quick-extract-images',
      format: 'png',
      scale: 2,
      downloadImages: true,
      generateReport: true,
      ...config
    };
    this.logger = new Logger({ traceId: uuidv4() });
  }

  /**
   * 快速提取图片 - 主要API方法
   */
  async quickExtract(): Promise<QuickExtractResult> {
    const startTime = Date.now();
    this.logger.info('🚀 开始QuickExtract图片提取', { 
      fileKey: this.config.fileKey,
      nodeId: this.config.nodeId 
    });

    try {
      // 1. 获取Figma数据
      const figmaData = await this.fetchFigmaFile();
      
      // 2. 扫描并提取图片节点
      this.extractedImages = this.findImageNodes(figmaData);
      this.logger.info(`🎯 发现 ${this.extractedImages.length} 个图片节点`);

      if (this.extractedImages.length === 0) {
        return this.createEmptyResult(figmaData, Date.now() - startTime);
      }

      // 3. 获取图片URL
      const imageUrls = await this.fetchImageUrls();
      this.assignImageUrls(imageUrls);

      // 4. 下载图片（如果启用）
      let downloadCount = 0;
      if (this.config.downloadImages) {
        downloadCount = await this.downloadImages();
      }

      // 5. 设置quickExtract链接
      this.setQuickExtractLinks();

      // 6. 生成增强数据
      const enhancedData = this.createEnhancedData(figmaData);

      // 7. 生成报告（如果启用）
      const outputPaths = await this.generateOutputFiles(enhancedData);

      const processingTime = Date.now() - startTime;
      const result: QuickExtractResult = {
        images: this.extractedImages,
        enhancedData,
        stats: {
          totalImages: this.extractedImages.length,
          successfulDownloads: downloadCount,
          failedDownloads: this.extractedImages.filter(img => img.imageUrl && !img.localPath).length,
          processingTime
        },
        outputPaths
      };

      this.logger.info('✅ QuickExtract图片提取完成', { 
        stats: result.stats 
      });

      return result;

    } catch (error) {
      this.logger.error('❌ QuickExtract图片提取失败', { error });
      throw error;
    }
  }

  /**
   * 获取Figma文件数据
   */
  private async fetchFigmaFile(): Promise<any> {
    this.logger.info('📡 正在获取Figma文件数据...');
    
    let url = `https://api.figma.com/v1/files/${this.config.fileKey}`;
    if (this.config.nodeId) {
      url += `?ids=${encodeURIComponent(this.config.nodeId)}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Figma-Token': this.config.accessToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Figma API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    this.logger.info('✅ Figma数据获取成功');
    return data;
  }

  /**
   * 递归查找图片节点
   */
  private findImageNodes(figmaData: any): QuickExtractImage[] {
    const images: QuickExtractImage[] = [];

    const traverse = (node: any, path: string = '') => {
      const currentPath = path ? `${path} > ${node.name}` : node.name;

      // 检查当前节点是否包含图片
      if (this.hasImageFill(node)) {
        const imageInfo = this.extractImageInfo(node);
        if (imageInfo) {
          images.push(imageInfo);
        }
      }

      // 递归检查子节点
      if (node.children && Array.isArray(node.children)) {
        for (const child of node.children) {
          traverse(child, currentPath);
        }
      }
    };

    // 处理不同的数据结构
    if (figmaData.document && figmaData.document.children) {
      // 标准document结构
      for (const page of figmaData.document.children) {
        traverse(page, `页面[${page.name}]`);
      }
    } else if (figmaData.nodes) {
      // nodes对象结构
      for (const [nodeId, nodeData] of Object.entries(figmaData.nodes)) {
        if (nodeData && typeof nodeData === 'object' && 'document' in nodeData) {
          const node = (nodeData as any).document;
          traverse(node, `节点[${node.name}]`);
        }
      }
    } else if (figmaData.name) {
      // 直接是节点数据
      traverse(figmaData, '根节点');
    }

    return images;
  }

  /**
   * 检查节点是否包含图片填充
   */
  private hasImageFill(node: any): boolean {
    if (!node.fills || !Array.isArray(node.fills)) {
      return false;
    }

    return node.fills.some((fill: any) => 
      fill.type === 'IMAGE' && (fill.imageRef || fill.imageHash)
    );
  }

  /**
   * 提取节点的图片信息
   */
  private extractImageInfo(node: any): QuickExtractImage | null {
    if (!node.fills || !Array.isArray(node.fills)) {
      return null;
    }

    const imageFill = node.fills.find((fill: any) => 
      fill.type === 'IMAGE' && (fill.imageRef || fill.imageHash)
    );

    if (!imageFill) {
      return null;
    }

    const bounds = node.absoluteBoundingBox || { x: 0, y: 0, width: 0, height: 0 };

    return {
      nodeId: node.id,
      nodeName: node.name || '未命名',
      nodeType: node.type || '未知',
      imageRef: imageFill.imageRef,
      imageHash: imageFill.imageHash,
      dimensions: {
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height
      }
    };
  }

  /**
   * 获取图片URL
   */
  private async fetchImageUrls(): Promise<Record<string, string>> {
    const nodeIds = this.extractedImages.map(img => img.nodeId);
    if (nodeIds.length === 0) {
      return {};
    }

    this.logger.info(`🔗 正在获取 ${nodeIds.length} 个图片的URL...`);

    const params = new URLSearchParams({
      format: this.config.format!,
      scale: this.config.scale!.toString(),
      ids: nodeIds.join(',')
    });

    const url = `https://api.figma.com/v1/images/${this.config.fileKey}?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Figma-Token': this.config.accessToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Figma Images API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    this.logger.info(`✅ 成功获取 ${Object.keys(data.images || {}).length} 个图片URL`);
    
    return data.images || {};
  }

  /**
   * 为图片分配URL
   */
  private assignImageUrls(imageUrls: Record<string, string>): void {
    for (const image of this.extractedImages) {
      const imageUrl = imageUrls[image.nodeId];
      if (imageUrl) {
        image.imageUrl = imageUrl;
      }
    }
  }

  /**
   * 下载图片到本地
   */
  private async downloadImages(): Promise<number> {
    this.logger.info('📥 开始下载图片...');
    
    // 确保输出目录存在
    const imagesDir = path.join(process.cwd(), this.config.outputDir!);
    if (!fs.existsSync(imagesDir)) {
      fs.mkdirSync(imagesDir, { recursive: true });
    }

    let downloadCount = 0;
    
    for (let i = 0; i < this.extractedImages.length; i++) {
      const image = this.extractedImages[i];
      if (image.imageUrl) {
        const filename = `${String(i + 1).padStart(3, '0')}_${image.nodeId.replace(':', '-')}_${image.nodeName.replace(/[^a-zA-Z0-9]/g, '_')}.${this.config.format}`;
        const localPath = await this.downloadSingleImage(image.imageUrl, filename, imagesDir);
        if (localPath) {
          image.localPath = localPath;
          downloadCount++;
        }
      }
    }

    this.logger.info(`✅ 图片下载完成: ${downloadCount}/${this.extractedImages.length}`);
    return downloadCount;
  }

  /**
   * 下载单个图片
   */
  private async downloadSingleImage(imageUrl: string, filename: string, outputDir: string): Promise<string | null> {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const buffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      const filePath = path.join(outputDir, filename);
      
      fs.writeFileSync(filePath, uint8Array);
      return filePath;
    } catch (error) {
      this.logger.warn(`下载失败: ${filename}`, { error });
      return null;
    }
  }

  /**
   * 设置quickExtract链接
   */
  private setQuickExtractLinks(): void {
    for (const image of this.extractedImages) {
      if (image.localPath) {
        // 优先使用本地路径
        image.quickExtract = image.localPath;
      } else if (image.imageUrl) {
        // fallback到URL
        image.quickExtract = image.imageUrl;
      }
    }
  }

  /**
   * 创建增强数据（原始数据 + images属性）
   */
  private createEnhancedData(originalData: any): any {
    return {
      ...originalData,
      images: this.extractedImages.map((img) => ({
        nodeId: img.nodeId,
        nodeName: img.nodeName,
        nodeType: img.nodeType,
        dimensions: img.dimensions,
        imageHash: img.imageHash,
        imageUrl: img.imageUrl,
        quickExtract: img.quickExtract
      }))
    };
  }

  /**
   * 生成输出文件
   */
  private async generateOutputFiles(enhancedData: any): Promise<QuickExtractResult['outputPaths']> {
    if (!this.config.generateReport) {
      return {};
    }

    const outputDir = path.join(process.cwd(), 'output');
    if (!fs.existsSync(outputDir)) {
      fs.mkdirSync(outputDir, { recursive: true });
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const outputPaths: QuickExtractResult['outputPaths'] = {};

    // 保存增强报告
    const reportPath = path.join(outputDir, `quick-extract-report-${timestamp}.json`);
    fs.writeFileSync(reportPath, JSON.stringify(enhancedData, null, 2));
    outputPaths.reportFile = reportPath;

    // 保存图片清单
    const imageList = this.extractedImages.map((img, index) => ({
      序号: index + 1,
      节点名称: img.nodeName,
      节点ID: img.nodeId,
      节点类型: img.nodeType,
      尺寸: `${img.dimensions.width} × ${img.dimensions.height}`,
      位置: `(${img.dimensions.x}, ${img.dimensions.y})`,
      本地文件: img.localPath ? path.basename(img.localPath) : '未下载',
      quickExtract: img.quickExtract || '无',
      状态: img.localPath ? '✅ 已下载' : (img.imageUrl ? '⚠️  仅URL' : '❌ 失败')
    }));

    const listPath = path.join(outputDir, `quick-extract-list-${timestamp}.json`);
    fs.writeFileSync(listPath, JSON.stringify(imageList, null, 2));
    outputPaths.listFile = listPath;

    // 保存统计摘要
    const summary = {
      extractionTime: new Date().toISOString(),
      extractionMethod: 'QuickExtract API',
      config: {
        fileKey: this.config.fileKey,
        nodeId: this.config.nodeId,
        format: this.config.format,
        scale: this.config.scale,
        downloadImages: this.config.downloadImages
      },
      summary: {
        totalImages: this.extractedImages.length,
        successfulDownloads: this.extractedImages.filter(img => img.localPath).length,
        failedDownloads: this.extractedImages.filter(img => !img.localPath && img.imageUrl).length,
        noUrlImages: this.extractedImages.filter(img => !img.imageUrl).length
      }
    };

    const summaryPath = path.join(outputDir, `quick-extract-summary-${timestamp}.json`);
    fs.writeFileSync(summaryPath, JSON.stringify(summary, null, 2));
    outputPaths.summaryFile = summaryPath;

    if (this.config.downloadImages) {
      outputPaths.imagesDir = path.join(process.cwd(), this.config.outputDir!);
    }

    this.logger.info('📝 输出文件生成完成', { outputPaths });
    return outputPaths;
  }

  /**
   * 创建空结果
   */
  private createEmptyResult(figmaData: any, processingTime: number): QuickExtractResult {
    return {
      images: [],
      enhancedData: { ...figmaData, images: [] },
      stats: {
        totalImages: 0,
        successfulDownloads: 0,
        failedDownloads: 0,
        processingTime
      },
      outputPaths: {}
    };
  }
}

/**
 * 快速提取图片的便捷函数
 */
export async function quickExtractImages(config: QuickExtractConfig): Promise<QuickExtractResult> {
  const processor = new QuickExtractImageProcessor(config);
  return await processor.quickExtract();
}